<template>
  <v-card v-if="chart && chart.type === 'graphic'" class="fill-height">
    <v-card-title class="text-subtitle-1 pb-0">{{ info.title }}</v-card-title>
    <v-card-text>
      <Plotly
        ref="mapPlot"
        class="PlotlyChart"
        :class="{ 'hide-modebar': !showModeBar }"
        :data="[
          {
            z: contentOfChartInJSON,
            type: 'heatmap',
            hoverinfo: 'skip',
            ...mapStyle,
            ...heatmapAxes
          },
          ...labelTraces
        ]"
        style="width: 100%; padding-bottom: 100%"
        :display-mode-bar="isFieldMap || showModeBar"
        :displaylogo="false"
        :to-image-button-options="imageOptions('png')"
        :layout="{
          ...layout,
          font: { size: 14 },
          margin: { l: 60, r: isFieldMap ? 10 : 8, t: 16, b: 56 },
          xaxis: { title: { text: 'x (µm)', font: { size: 16 } }, tickfont: { size: 14 }, ticks: 'outside', zeroline: false, showgrid: false },
          yaxis: { title: { text: 'y (µm)', font: { size: 16 } }, tickfont: { size: 14 }, ticks: 'outside', zeroline: false, showgrid: false, scaleanchor: 'x' },
          showlegend: false,
          annotations: labelAnnotations,
          responsive: true
        }"
        :display-responsive="true"
        :responsive="true"
        :options-responsive="true"
      ></Plotly>
      <p class="text-caption mb-0">{{ info.caption }}</p>
    </v-card-text>
    <!-- Figura para artigo: PNG em 3× a resolução da tela ou SVG (vetorial) -->
    <v-card-actions v-if="isFieldMap" class="pt-0">
      <v-spacer />
      <v-btn small text color="primary" @click="downloadFigure('png')">
        <v-icon left small>mdi-download</v-icon> PNG (3×)
      </v-btn>
      <v-btn small text color="primary" @click="downloadFigure('svg')">
        <v-icon left small>mdi-download</v-icon> SVG
      </v-btn>
    </v-card-actions>
  </v-card>
  <v-card v-else-if="chart && chart.type === 'movie'" class="fill-height">
    <v-card-title class="text-subtitle-1">Field video</v-card-title>
    <v-card-text>
      <video width="100%" controls loop playsinline :src="movieURL"></video>
      <p class="text-caption mb-0">Evolution of the field in time, from the start of the source.</p>
    </v-card-text>
  </v-card>
  <v-card v-else-if="chart && chart.type === 'flux'" class="fill-height">
    <v-card-title class="text-subtitle-1 pb-0">
      {{ contentOfChartInJSON.name || chart.title }}
    </v-card-title>
    <v-card-text>
      <Plotly
        v-if="spectrumRows.length"
        :data="spectrumRows"
        style="width: 100%; padding-bottom: 80%"
        :display-mode-bar="showModeBar"
        :displaylogo="false"
        :layout="{
          ...layout,
          margin: { l: 52, r: 8, t: 16, b: 44 },
          xaxis: { title: contentOfChartInJSON.xlabel || 'Frequency (c/µm = 1/λ)' },
          yaxis: { title: 'Fraction of incident power', rangemode: 'tozero' },
          legend: { orientation: 'h' },
          responsive: true
        }"
        :display-responsive="true"
        :responsive="true"
        :options-responsive="true"
      ></Plotly>
      <p class="text-caption mb-0">
        1 = all the incident power. Wavelength λ = 1 / frequency (µm); hover or tap a point to
        see it. Loss = 1 − R − T (power absorbed or scattered away from the outputs).
      </p>
      <v-alert v-if="spectrumAboveOne" dense text type="info" class="text-caption mt-2 mb-0">
        Values above 1 appear where the reference run carries almost no power (for example,
        outside the band of the reference waveguide). They are a normalization artifact, not a
        gain: read the spectrum only where the reference transmits.
      </v-alert>
    </v-card-text>
  </v-card>
  <v-card v-else-if="chart && chart.type === 'resonance'" class="fill-height">
    <v-card-title class="text-subtitle-1 pb-0">{{ chart.title }}</v-card-title>
    <v-card-subtitle class="pt-1">Resonances found by Harminv</v-card-subtitle>
    <v-card-text>
      <v-simple-table v-if="chart.content.modes && chart.content.modes.length" dense>
        <thead>
          <tr>
            <th>f₀ (c/µm)</th>
            <th>λ₀ (µm)</th>
            <th>Q</th>
            <th>Amplitude</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(mode, index) in sortedModes" :key="index">
            <td>{{ mode.frequency.toFixed(5) }}</td>
            <td>{{ mode.wavelength ? mode.wavelength.toFixed(4) : "-" }}</td>
            <td>{{ mode.Q.toFixed(1) }}</td>
            <td>{{ mode.amplitude.toExponential(2) }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <p v-else class="mb-0">
        No resonance found in this band. Check that the point is inside the resonator and
        that the band (center λ and width) covers the expected mode.
      </p>
      <p class="text-caption mt-2 mb-0">
        Q = f₀ / Δf (linewidth): higher Q means light stays longer in the resonator.
        Modes are sorted by amplitude at the measurement point.
      </p>
    </v-card-text>
  </v-card>
  <div v-else>
    <v-row justify="center" align="center" class="fill-height">
      <v-progress-circular
        :size="100"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-row>
  </div>
</template>
<script>
import { Plotly } from "vue-plotly";
import simulator from "@/services/simulator";
import { serializeMonitor, serializeSource } from "@/store/module-simulator/lib/monitors";
import Swal from "sweetalert2";
export default {
  name: "DetailsChartCard",
  props: {
    chart: { type: Object, default: null },
    // Tamanho da célula em µm ({ x, y }), para os eixos dos mapas
    cell: { type: Object, default: null },
    // Estado salvo na produção: fontes e saídas para a opção "Labels"
    production: { type: Object, default: null }
  },
  data: () => ({
    contentOfChartInJSON: [],
    layout: { font: { size: 13 } }
  }),
  computed: {
    isFieldMap() {
      return this.chart && this.chart.title === "Simulação Gerada";
    },
    // Componente do campo escolhido nas opções da simulação (Ez, Hz, ...)
    fieldComponent() {
      const options = this.production && this.production.plotOptions;
      return (options && typeof options.plotAxis === "string" && options.plotAxis) || "E";
    },
    // Campo: escala divergente simétrica em torno de zero (azul < 0 < vermelho, como nos mapas do Meep)
    // e barra de cores com o componente; estrutura: tons de cinza sem barra
    mapStyle() {
      if (!this.isFieldMap) {
        return { colorscale: this.colorScale(this.chart), showscale: false };
      }
      const values = (this.contentOfChartInJSON || []).flat().filter(v => Number.isFinite(v));
      const peak = values.reduce((max, v) => Math.max(max, Math.abs(v)), 0) || 1;
      return {
        colorscale: [[0, "#2166ac"], [0.25, "#67a9cf"], [0.5, "#f7f7f7"], [0.75, "#ef8a62"], [1, "#b2182b"]],
        zmin: -peak,
        zmax: peak,
        zmid: 0,
        showscale: true,
        colorbar: {
          title: { text: `${this.fieldComponent} (a.u.)`, side: "right", font: { size: 15 } },
          tickfont: { size: 13 },
          exponentformat: "power",
          thickness: 14,
          len: 0.9
        }
      };
    },
    // Títulos gravados pelas versões antigas (em português) e explicação de cada mapa
    info() {
      const title = this.chart.title;
      if (title === "Dieletrico") {
        return {
          title: "Structure (permittivity ε)",
          caption: "Dark = higher ε (denser material). Check that the geometries are where you expect."
        };
      }
      if (title === "Simulação Gerada") {
        return {
          title: "Field at the end of the run",
          caption: "Snapshot of the field component chosen in the plot options. Red and blue are opposite signs; white is zero."
        };
      }
      return { title, caption: "" };
    },
    // Opção "Labels": fontes (pontos vermelhos) e saídas (linhas azuis) com o nome, em µm
    labelTraces() {
      const production = this.production;
      if (!production || !production.plotOptions || production.plotOptions.labels !== 1 || !production.dimensions) {
        return [];
      }
      const traces = [];
      (production.SourcesList || []).forEach((source) => {
        try {
          const { position, waveSize, name } = serializeSource(production, source);
          const [w, h] = waveSize;
          traces.push({
            x: w || h ? [position[0] - w / 2, position[0] + w / 2] : [position[0]],
            y: w || h ? [position[1] - h / 2, position[1] + h / 2] : [position[1]],
            mode: w || h ? "lines" : "markers",
            line: { color: "#e53935", width: 3 },
            marker: { color: "#e53935", size: 9 },
            hoverinfo: "text",
            text: name || "Source",
            type: "scatter"
          });
        } catch (err) {
          // produção sem a escala do canvas
        }
      });
      (production.FluxList || []).forEach((monitor) => {
        try {
          const { center, size, name } = serializeMonitor(production, monitor);
          const [w, h] = size;
          traces.push({
            x: w || h ? [center[0] - w / 2, center[0] + w / 2] : [center[0]],
            y: w || h ? [center[1] - h / 2, center[1] + h / 2] : [center[1]],
            mode: w || h ? "lines" : "markers",
            line: { color: "#1e88e5", width: 3 },
            marker: { color: "#1e88e5", size: 9, symbol: "circle-open" },
            hoverinfo: "text",
            text: name || "Output",
            type: "scatter"
          });
        } catch (err) {
          // monitor no formato antigo
        }
      });
      return traces;
    },
    // Nomes em caixas brancas (legíveis sobre o campo); fonte acima, saídas abaixo do ponto
    labelAnnotations() {
      return this.labelTraces.map((trace, index) => {
        const n = trace.x.length;
        const isSource = trace.line.color === "#e53935";
        return {
          x: trace.x[n - 1],
          y: trace.y[n - 1],
          text: trace.text,
          showarrow: true,
          arrowhead: 0,
          ax: 24,
          ay: isSource ? -22 - 6 * (index % 2) : 22 + 6 * (index % 2),
          bgcolor: "rgba(255,255,255,0.85)",
          bordercolor: trace.line.color,
          font: { size: 11, color: "#111" }
        };
      });
    },
    // Eixos em µm com a origem no centro da célula (o mapa tem uma linha por pixel em y)
    heatmapAxes() {
      const z = this.contentOfChartInJSON;
      if (!this.cell || !Array.isArray(z) || !z.length || !Array.isArray(z[0])) return {};
      const width = Number(this.cell.x), height = Number(this.cell.y);
      const dx = width / z[0].length, dy = height / z.length;
      return { x0: -width / 2 + dx / 2, dx, y0: -height / 2 + dy / 2, dy };
    },
    // No celular não há "hover": a barra de ferramentas do Plotly só atrapalha
    // (o vue-plotly lê a configuração dos atributos do componente)
    showModeBar() {
      return !this.$vuetify.breakpoint.xsOnly;
    },
    spectrumAboveOne() {
      return this.spectrumRows.some(row => row.name !== "Loss (1 − R − T)" && (row.y || []).some(v => v > 1.05));
    },
    // Curvas do espectro (T, R e perda); aceita também o formato antigo row1..row3
    spectrumRows() {
      const content = this.contentOfChartInJSON || {};
      const rows = content.rows || ["row1", "row2", "row3"].map(key => content[key]).filter(Boolean);
      return rows.map(row => ({
        x: row.x,
        y: row.y,
        name: row.title,
        type: "scatter",
        mode: "lines",
        customdata: (row.x || []).map(f => (f ? 1 / f : null)),
        hovertemplate: "f = %{x:.4f} c/µm<br>λ = %{customdata:.4f} µm<br>%{y:.4f}<extra>%{fullData.name}</extra>"
      }));
    },
    sortedModes() {
      return [...(this.chart.content.modes || [])].sort((a, b) => b.amplitude - a.amplitude);
    },
    // O módulo 2D devolve o caminho como "./storage/users/..."; é servido em /meep/storage/...
    movieURL() {
      const path = String(this.chart.content || "").replace(/^\.?\//, "/");
      return encodeURI(`${process.env.VUE_APP_SIMULATOR_LOCATION}${path}`);
    }
  },
  methods: {
    // Tamanho da figura exportada: 1000 px de largura e a altura na proporção da célula
    imageOptions(format) {
      const width = 1000;
      const ratio = this.cell && this.cell.x ? Number(this.cell.y) / Number(this.cell.x) : 1;
      const title = (this.production && this.production.title) || "electrosfi";
      return {
        format,
        scale: format === "png" ? 3 : 1,
        width,
        height: Math.round(Math.max(0.4, Math.min(2.5, ratio)) * (width - 110) + 80),
        filename: `${title}-${this.fieldComponent}-field`.replace(/[^\w.-]+/g, "_")
      };
    },
    // Usa o botão "baixar" do próprio Plotly (o vue-plotly não expõe a função de download): o formato é
    // lido de gd._context na hora do clique. No celular a barra fica oculta, mas o botão continua no DOM.
    downloadFigure(format) {
      const plot = this.$refs.mapPlot && this.$refs.mapPlot.$el;
      if (!plot || !plot._context) return;
      plot._context.toImageButtonOptions = this.imageOptions(format);
      const button = plot.querySelector('.modebar-btn[data-title^="Download plot"]');
      if (button) button.click();
    },
    colorScale: function(obj) {
      return obj.title == "Dieletrico"
        ? [
            [0, "#FFF"],
            [1, "#000"]
          ]
        : [];
    },
    searchChartsFromProduction: async function() {
      try {
        const { content } = this.chart;
        const address = String(content).replace("./", "/");
        const { data } = await simulator.get(address);
        this.contentOfChartInJSON = await data;
      } catch (err) {
        Swal.fire({ title: "An error appears", text: err.message });
      }
    }
  },
  mounted() {
    // O vídeo é carregado pelo próprio <video> e as ressonâncias já vêm no próprio gráfico
    if (this.chart && ["movie", "resonance"].includes(this.chart.type)) return;
    this.searchChartsFromProduction();
  },
  components: { Plotly }
};
</script>

<style>
/* Celular: a barra do Plotly fica oculta (o download usa o botão dela pelos botões do cartão) */
.hide-modebar .modebar {
  display: none !important;
}
</style>
